import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import {
  DollarSign, Clock, AlertTriangle, Users, RefreshCw, Search,
  FileSpreadsheet, TrendingUp, Loader2, ShieldAlert, ChevronLeft, ChevronRight,
  Lock, Unlock, CheckCircle2, Download, Eye, Activity, Zap,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

export default function PayrollAdminPage() {
  const { user, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [flagOn, setFlagOn] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [interpreting, setInterpreting] = useState(false);
  const [weekLock, setWeekLock] = useState(null);
  const [lockAction, setLockAction] = useState(null); // 'review'|'lock'|'unlock'
  const [lockReason, setLockReason] = useState('');
  const [lockLoading, setLockLoading] = useState(false);
  const [validation, setValidation] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [watchdog, setWatchdog] = useState(null);
  const [health, setHealth] = useState(null);
  const [weekHealth, setWeekHealth] = useState(null);

  const hasView = hasPermission('staff.payroll.view') || hasPermission('payroll.view');
  const hasExport = hasPermission('payroll.export');

  // Check feature flag
  useEffect(() => {
    (async () => {
      try {
        const flags = await api.get('/production/feature-flags/mine');
        const d = flags.data?.data || flags.data || {};
        const enabled = !!d.payroll_engine_enabled && !!d.payroll_export_preview_enabled;
        setFlagOn(enabled);
      } catch {
        setFlagOn(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Load payroll weeks
  const loadWeeks = useCallback(async () => {
    try {
      const r = await api.get('/payroll/weeks?limit=26');
      setWeeks(r.data?.data || []);
      if (!selectedWeek && r.data?.data?.length) {
        setSelectedWeek(r.data.data[0].week_start);
      }
    } catch { /* non-fatal */ }
  }, [selectedWeek]);

  useEffect(() => {
    if (flagOn) loadWeeks();
  }, [flagOn, loadWeeks]);

  // Load watchdog + operations health
  useEffect(() => {
    if (!flagOn) return;
    (async () => {
      try {
        const [wd, oh] = await Promise.all([
          api.get('/payroll/watchdog/status').catch(() => null),
          api.get('/payroll/operations-health').catch(() => null),
        ]);
        setWatchdog(wd?.data?.data || null);
        setHealth(oh?.data?.data || null);
      } catch { /* non-fatal */ }
    })();
  }, [flagOn]);

  // Load export preview for selected week
  useEffect(() => {
    if (!selectedWeek || !flagOn) return;
    (async () => {
      setPreviewLoading(true);
      try {
        const [previewRes, lockRes, valRes, whRes] = await Promise.all([
          api.get(`/payroll/export-preview/${selectedWeek}`),
          api.get(`/payroll/week-lock/${selectedWeek}`).catch(() => null),
          api.get(`/payroll/export/validate/${selectedWeek}`).catch(() => null),
          api.get(`/payroll/operations-health/${selectedWeek}`).catch(() => null),
        ]);
        setPreview(previewRes.data?.data || null);
        setWeekLock(lockRes?.data?.data || null);
        setValidation(valRes?.data?.data || null);
        setWeekHealth(whRes?.data?.data || null);
      } catch (e) {
        if (e.response?.status !== 403) {
          notify.error(e, 'PAYROLL_LOAD_FAILED');
        }
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    })();
  }, [selectedWeek, flagOn]);

  const handleInterpret = async () => {
    if (!selectedWeek) return;
    setInterpreting(true);
    try {
      const r = await api.post('/payroll/interpret', { week_start: selectedWeek, force: false });
      const d = r.data?.data || {};
      toast.success(`Interpreted ${d.interpreted} timesheet(s), ${d.entries_created} entries created, ${d.skipped} skipped`);
      // Reload preview
      const [pr, lr, vr] = await Promise.all([
        api.get(`/payroll/export-preview/${selectedWeek}`),
        api.get(`/payroll/week-lock/${selectedWeek}`).catch(() => null),
        api.get(`/payroll/export/validate/${selectedWeek}`).catch(() => null),
      ]);
      setPreview(pr.data?.data || null);
      setWeekLock(lr?.data?.data || null);
      setValidation(vr?.data?.data || null);
      loadWeeks();
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.data?.message || 'Interpretation failed';
      notify.error(e, 'PAYROLL_LOAD_FAILED', 'Interpretation failed');
    } finally {
      setInterpreting(false);
    }
  };

  const handleLockAction = async () => {
    if (!selectedWeek || !lockAction) return;
    if (lockAction === 'unlock' && lockReason.trim().length < 5) {
      notify.warn('PAYROLL_UNLOCK_REASON_SHORT');
      return;
    }
    setLockLoading(true);
    try {
      const body = lockAction === 'unlock' ? { reason: lockReason.trim() } : { notes: lockReason.trim() || undefined };
      await api.post(`/payroll/week-lock/${selectedWeek}/${lockAction}`, body);
      toast.success(`Week ${lockAction === 'review' ? 'marked as reviewed' : lockAction === 'lock' ? 'locked' : 'unlocked'}`);
      const [lr, vr] = await Promise.all([
        api.get(`/payroll/week-lock/${selectedWeek}`),
        api.get(`/payroll/export/validate/${selectedWeek}`).catch(() => null),
      ]);
      setWeekLock(lr.data?.data || null);
      setValidation(vr?.data?.data || null);
    } catch (e) {
      notify.error(e, 'PAYROLL_LOCK_FAILED', `Failed to ${lockAction} week`);
    } finally {
      setLockLoading(false);
      setLockAction(null);
      setLockReason('');
    }
  };

  const handleCsvExport = async () => {
    if (!selectedWeek) return;
    setExporting(true);
    try {
      const r = await api.get(`/payroll/export/xero-csv/${selectedWeek}`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payroll_xero_${selectedWeek}_to_${preview?.week_end || ''}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
      // Refresh lock status
      const lr = await api.get(`/payroll/week-lock/${selectedWeek}`);
      setWeekLock(lr.data?.data || null);
    } catch (e) {
      const errData = e.response?.data;
      if (errData && typeof errData === 'object' && errData.errors) {
        notify.error(e, 'PAYROLL_EXPORT_VALIDATION');
      } else {
        // Try parsing blob error
        try {
          const text = await e.response?.data?.text?.();
          const parsed = JSON.parse(text);
          notify.error(e, 'PAYROLL_EXPORT_FAILED', 'Export failed');
        } catch {
          notify.error(e, 'PAYROLL_EXPORT_FAILED');
        }
      }
    } finally {
      setExporting(false);
    }
  };

  // Navigate weeks
  const goWeek = (delta) => {
    const idx = weeks.findIndex(w => w.week_start === selectedWeek);
    const target = weeks[idx + delta];
    if (target) setSelectedWeek(target.week_start);
  };

  // Filter staff
  const filteredStaff = (preview?.staff || []).filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.staff_name?.toLowerCase().includes(q) || s.payroll_external_id?.toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!hasView) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md"><CardContent className="p-8 text-center">
          <ShieldAlert className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You do not have permission to view payroll data.</p>
        </CardContent></Card>
      </div>
    );
  }

  if (flagOn === false) {
    return (
      <div className="p-6 max-w-4xl space-y-4" data-testid="payroll-admin-page">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <DollarSign className="w-5 h-5" /> Payroll Engine
        </h1>
        <Alert className="border-amber-300 bg-amber-50">
          <ShieldAlert className="w-4 h-4" />
          <AlertDescription>
            Payroll Engine is not enabled. Enable both <b>payroll_engine_enabled</b> and <b>payroll_export_preview_enabled</b> feature flags to access this page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const totals = preview?.totals || {};

  return (
    <div className="p-6 max-w-7xl space-y-5" data-testid="payroll-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <DollarSign className="w-5 h-5" /> Payroll Engine
          </h1>
          <p className="text-sm text-muted-foreground mt-1">SCHADS-style payroll interpretation. Operational only — does not affect invoices or funding.</p>
        </div>
        {hasExport && selectedWeek && (
          <Button onClick={handleInterpret} disabled={interpreting} size="sm" data-testid="interpret-btn">
            {interpreting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            {interpreting ? 'Interpreting...' : 'Run Interpretation'}
          </Button>
        )}
      </div>

      {/* Week Selector */}
      <div className="flex items-center gap-3" data-testid="week-selector">
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => goWeek(1)} disabled={weeks.findIndex(w => w.week_start === selectedWeek) >= weeks.length - 1}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Select value={selectedWeek} onValueChange={setSelectedWeek}>
          <SelectTrigger className="w-[280px] h-8 text-sm" data-testid="week-select">
            <SelectValue placeholder="Select payroll week" />
          </SelectTrigger>
          <SelectContent>
            {weeks.map(w => (
              <SelectItem key={w.week_start} value={w.week_start}>
                {w.week_start} — {w.week_end} ({w.staff_count} staff, ${w.total_amount})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => goWeek(-1)} disabled={weeks.findIndex(w => w.week_start === selectedWeek) <= 0}>
          <ChevronRight className="w-4 h-4" />
        </Button>
        {preview && (
          <Badge variant="outline" className="text-xs ml-auto">
            {preview.week_start} — {preview.week_end}
          </Badge>
        )}
      </div>

      {/* Lock Status Bar */}
      {weekLock && (
        <Card className={`border-l-4 ${
          weekLock.status === 'EXPORTED' ? 'border-l-emerald-500 bg-emerald-50/30' :
          weekLock.status === 'LOCKED' ? 'border-l-red-500 bg-red-50/30' :
          weekLock.status === 'REVIEWED' ? 'border-l-blue-500 bg-blue-50/30' :
          'border-l-slate-300 bg-slate-50/30'
        }`} data-testid="week-lock-status">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Badge className={`text-[10px] ${
                weekLock.status === 'EXPORTED' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' :
                weekLock.status === 'LOCKED' ? 'bg-red-100 text-red-800 border-red-300' :
                weekLock.status === 'REVIEWED' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                'bg-slate-100 text-slate-700 border-slate-300'
              }`} data-testid="week-status-badge">
                {weekLock.status === 'LOCKED' && <Lock className="w-2.5 h-2.5 mr-0.5" />}
                {weekLock.status === 'EXPORTED' && <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />}
                {weekLock.status === 'REVIEWED' && <Eye className="w-2.5 h-2.5 mr-0.5" />}
                {weekLock.status}
              </Badge>
              {weekLock.notes && <span className="text-xs text-muted-foreground">{weekLock.notes}</span>}
              {weekLock.exported_at && <span className="text-[10px] text-muted-foreground">Exported: {weekLock.exported_at?.slice(0, 10)} via {weekLock.export_provider}</span>}
            </div>
            <div className="flex items-center gap-2">
              {weekLock.status === 'DRAFT' && hasExport && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setLockAction('review'); setLockReason(''); }} data-testid="btn-review-week">
                  <Eye className="w-3 h-3 mr-1" />Mark Reviewed
                </Button>
              )}
              {weekLock.status === 'REVIEWED' && hasExport && (
                <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50" onClick={() => { setLockAction('lock'); setLockReason(''); }} data-testid="btn-lock-week">
                  <Lock className="w-3 h-3 mr-1" />Lock Week
                </Button>
              )}
              {(weekLock.status === 'LOCKED' || weekLock.status === 'EXPORTED') && hasExport && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setLockAction('unlock'); setLockReason(''); }} data-testid="btn-unlock-week">
                  <Unlock className="w-3 h-3 mr-1" />Unlock
                </Button>
              )}
              {weekLock.status === 'LOCKED' && hasExport && validation?.valid && (
                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={handleCsvExport} disabled={exporting} data-testid="btn-export-csv">
                  {exporting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                  {exporting ? 'Exporting...' : 'Generate Xero CSV'}
                </Button>
              )}
              {weekLock.status === 'LOCKED' && hasExport && validation && !validation.valid && (
                <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-700 bg-amber-50" data-testid="export-blocked-badge">
                  <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Export blocked: {validation.errors?.[0]}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {previewLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading payroll data...
        </div>
      ) : preview ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="payroll-summary-cards">
            <Card><CardContent className="p-3 text-center">
              <Users className="w-4 h-4 mx-auto mb-1 text-slate-500" />
              <p className="text-xl font-bold" data-testid="kpi-staff-count">{preview.staff_count}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Staff</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <Clock className="w-4 h-4 mx-auto mb-1 text-blue-500" />
              <p className="text-xl font-bold" data-testid="kpi-ordinary-hours">{totals.ordinary_hours || 0}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Ordinary hrs</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <TrendingUp className="w-4 h-4 mx-auto mb-1 text-orange-500" />
              <p className="text-xl font-bold" data-testid="kpi-overtime-hours">{totals.overtime_hours || 0}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Overtime hrs</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-xl font-bold" data-testid="kpi-saturday-hours">{totals.saturday_hours || 0}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Saturday hrs</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-xl font-bold" data-testid="kpi-sunday-hours">{totals.sunday_hours || 0}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Sunday hrs</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-xl font-bold" data-testid="kpi-ph-hours">{totals.public_holiday_hours || 0}</p>
              <p className="text-[10px] text-muted-foreground uppercase">PH hrs</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-xl font-bold" data-testid="kpi-allowances">${totals.allowances || 0}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Allowances</p>
            </CardContent></Card>
            <Card className="border-emerald-300 bg-emerald-50/40"><CardContent className="p-3 text-center">
              <DollarSign className="w-4 h-4 mx-auto mb-1 text-emerald-600" />
              <p className="text-xl font-bold text-emerald-700" data-testid="kpi-gross-pay">${totals.gross_pay || 0}</p>
              <p className="text-[10px] text-emerald-700 uppercase">Gross Pay</p>
            </CardContent></Card>
          </div>

          {/* Search */}
          <div className="flex items-center gap-3">
            <div className="relative w-[280px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search staff or external ID..."
                value={search} onChange={e => setSearch(e.target.value)}
                className="pl-9 h-8 text-sm"
                data-testid="payroll-search"
              />
            </div>
            <Badge variant="outline" className="text-xs">
              {filteredStaff.length} staff
            </Badge>
          </div>

          {/* Export Preview Table */}
          <Card data-testid="export-preview-table">
            <CardHeader className="pb-2 px-4 pt-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4" /> Export Preview
                <Badge className="bg-amber-200 text-amber-900 border-amber-400 text-[9px] ml-2">PREVIEW ONLY</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Staff</TableHead>
                    <TableHead className="text-xs">External ID</TableHead>
                    <TableHead className="text-xs text-right">Ordinary</TableHead>
                    <TableHead className="text-xs text-right">Overtime</TableHead>
                    <TableHead className="text-xs text-right">Saturday</TableHead>
                    <TableHead className="text-xs text-right">Sunday</TableHead>
                    <TableHead className="text-xs text-right">PH</TableHead>
                    <TableHead className="text-xs text-right">Allowances</TableHead>
                    <TableHead className="text-xs text-right">Gross Pay</TableHead>
                    <TableHead className="text-xs text-center">Ready</TableHead>
                    <TableHead className="text-xs">Warnings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStaff.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center text-muted-foreground py-6 text-sm">
                        No staff payroll data for this week.
                      </TableCell>
                    </TableRow>
                  ) : filteredStaff.map(s => (
                    <TableRow key={s.staff_id} data-testid={`payroll-row-${s.staff_id}`}>
                      <TableCell className="text-sm font-medium">{s.staff_name || '—'}</TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground">{s.payroll_external_id || '—'}</TableCell>
                      <TableCell className="text-xs text-right font-mono">{s.ordinary_hours}</TableCell>
                      <TableCell className="text-xs text-right font-mono">{s.overtime_hours > 0 ? <span className="text-orange-600 font-semibold">{s.overtime_hours}</span> : '0'}</TableCell>
                      <TableCell className="text-xs text-right font-mono">{s.saturday_hours > 0 ? s.saturday_hours : '0'}</TableCell>
                      <TableCell className="text-xs text-right font-mono">{s.sunday_hours > 0 ? s.sunday_hours : '0'}</TableCell>
                      <TableCell className="text-xs text-right font-mono">{s.public_holiday_hours > 0 ? s.public_holiday_hours : '0'}</TableCell>
                      <TableCell className="text-xs text-right font-mono">${s.allowances || 0}</TableCell>
                      <TableCell className="text-xs text-right font-mono font-semibold">${s.gross_pay}</TableCell>
                      <TableCell className="text-center">
                        {s.export_ready ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[9px]" data-testid="export-ready-badge">Ready</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 border-red-300 text-[9px]" data-testid="export-not-ready-badge">Not Ready</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {s.warnings?.length > 0 && (
                          <div className="flex flex-col gap-0.5">
                            {s.warnings.map((w, i) => (
                              <Badge key={i} variant="outline" className="text-[8px] text-amber-700 border-amber-300 bg-amber-50" data-testid="export-warning-badge">
                                <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />{w}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Safety footer */}
          {/* Watchdog Status + Warnings */}
          {(watchdog || weekHealth) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="watchdog-section">
              {/* Watchdog Status Card */}
              {watchdog && (
                <Card>
                  <CardHeader className="pb-2 px-4 pt-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Activity className="w-4 h-4 text-emerald-600" /> Payroll Watchdog
                      {watchdog.running ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[9px]" data-testid="watchdog-running">Running</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 border-slate-300 text-[9px]">Stopped</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-muted-foreground">Interval:</span> <span className="font-medium">Every {watchdog.interval_minutes} min</span></div>
                      <div><span className="text-muted-foreground">Last Scan:</span> <span className="font-medium" data-testid="watchdog-last-scan">{watchdog.last_scan_at?.slice(11, 19) || 'Pending'}</span></div>
                    </div>
                    {health && (
                      <div className="grid grid-cols-4 gap-2 text-xs pt-1 border-t">
                        <div className="text-center">
                          <p className="text-base font-bold text-emerald-600" data-testid="health-ready">{health.ready_exports}</p>
                          <p className="text-[9px] text-muted-foreground">Ready</p>
                        </div>
                        <div className="text-center">
                          <p className="text-base font-bold text-red-600" data-testid="health-blocked">{health.blocked_exports}</p>
                          <p className="text-[9px] text-muted-foreground">Blocked</p>
                        </div>
                        <div className="text-center">
                          <p className="text-base font-bold text-amber-600" data-testid="health-warnings">{health.total_warnings}</p>
                          <p className="text-[9px] text-muted-foreground">Warnings</p>
                        </div>
                        <div className="text-center">
                          <p className="text-base font-bold text-slate-500" data-testid="health-not-ready">{health.not_ready_exports}</p>
                          <p className="text-[9px] text-muted-foreground">Not Ready</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Week Warnings Card */}
              {weekHealth && weekHealth.warnings?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2 px-4 pt-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-500" /> Week Warnings
                      <Badge className={`text-[9px] ${
                        weekHealth.status === 'READY_FOR_EXPORT' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
                        weekHealth.status === 'BLOCKED' ? 'bg-red-100 text-red-700 border-red-300' :
                        weekHealth.status === 'WARNING' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                        'bg-slate-100 text-slate-600 border-slate-300'
                      }`} data-testid="week-readiness-badge">{weekHealth.status?.replace(/_/g, ' ')}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-[10px] w-16">Severity</TableHead>
                          <TableHead className="text-[10px]">Type</TableHead>
                          <TableHead className="text-[10px]">Message</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {weekHealth.warnings.map((w, i) => (
                          <TableRow key={i}>
                            <TableCell>
                              <Badge className={`text-[8px] ${
                                w.severity === 'HIGH' ? 'bg-red-100 text-red-700 border-red-300' :
                                w.severity === 'MEDIUM' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                                'bg-slate-100 text-slate-600 border-slate-300'
                              }`} data-testid="warning-severity-badge">{w.severity}</Badge>
                            </TableCell>
                            <TableCell className="text-[10px] font-mono">{w.type}</TableCell>
                            <TableCell className="text-[10px]">{w.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <Alert className="border-slate-200 bg-slate-50">
            <AlertDescription className="text-[11px] text-slate-600">
              This is a <b>preview only</b>. No data has been submitted to any payroll provider. Payroll interpretation is operational — it does not affect invoices, funding, or NDIS claims.
            </AlertDescription>
          </Alert>
        </>
      ) : (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>No payroll data. Select a week or run interpretation first.</AlertDescription>
        </Alert>
      )}

      {/* Lock/Unlock/Review Dialog */}
      <Dialog open={!!lockAction} onOpenChange={(v) => { if (!v) { setLockAction(null); setLockReason(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {lockAction === 'review' && 'Mark Week as Reviewed'}
              {lockAction === 'lock' && 'Lock Payroll Week'}
              {lockAction === 'unlock' && 'Unlock Payroll Week'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {lockAction === 'unlock' && (
              <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded">Unlocking will allow re-interpretation and changes. Previously exported data may become stale.</p>
            )}
            {lockAction === 'lock' && (
              <p className="text-sm text-red-700 bg-red-50 p-2 rounded">Locking prevents changes to payroll entries. You can still export CSV after locking.</p>
            )}
            <div>
              <Label>{lockAction === 'unlock' ? 'Reason (required)' : 'Notes (optional)'}</Label>
              <Textarea
                value={lockReason}
                onChange={e => setLockReason(e.target.value)}
                placeholder={lockAction === 'unlock' ? 'Why are you unlocking this week?' : 'Optional notes...'}
                className="mt-1"
                data-testid="lock-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setLockAction(null); setLockReason(''); }}>Cancel</Button>
            <Button onClick={handleLockAction} disabled={lockLoading || (lockAction === 'unlock' && lockReason.trim().length < 5)} data-testid="confirm-lock-action">
              {lockLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
              {lockAction === 'review' ? 'Confirm Review' : lockAction === 'lock' ? 'Lock Week' : 'Unlock Week'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
